import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Section } from './DetailPrimitives';
import { conceptHref, conceptTypeLabel, formatValue, parameterHref } from './ontologyFormat';
import {
    conceptsApi, getPolicyParameters, normalizeList, ontologyErrorMessage,
} from '../../api/ontologyApi';
import { dsVar, ink } from '../../theme/tokens';

interface CoverageRow {
    conceptCode: string;
    label: string;
    conceptType?: string;
    parameters: Record<string, any>[];
}

const mono = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.6875rem',
    color: dsVar('fg-muted'),
};

/**
 * The thresholds a policy sets, and — just as important — the ones it does not.
 *
 * Policy → Parameter is a true 1:N: every parameter names exactly one policyCode, so
 * rooting the drill-down here is unambiguous. What it is NOT is a level of a tree.
 * `governsConcepts` is many-to-many, so the same concept can hold parameters under
 * several policies (structuring_threshold already does, under the org's TM standard and
 * the US.BSA.CTR baseline). This section therefore reads DOWN from one policy and makes
 * no claim about the global shape.
 *
 * The unset rows are the point. On the dev instance TM.STANDARD governs 48 concepts and
 * configures 25; six other policies govern concepts and configure nothing at all. Listing
 * only what exists would render those as an empty section — indistinguishable from a
 * failed load — instead of as the coverage gap they are.
 */
const PolicyParametersSection: React.FC<{
    policyCode: string;
    governsConcepts?: unknown;
}> = ({ policyCode, governsConcepts }) => {
    const [parameters, setParameters] = useState<Record<string, any>[]>([]);
    const [labels, setLabels] = useState<Record<string, Record<string, any>>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const governed = useMemo(
        () => (Array.isArray(governsConcepts) ? governsConcepts.filter(Boolean).map(String) : []),
        [governsConcepts],
    );

    useEffect(() => {
        if (!policyCode) return undefined;
        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                // The parameters, and the labels for every governed concept. The second
                // call is what lets an unset concept be shown by name rather than as a
                // bare code — ?codes= resolves the whole list in one read.
                const [paramRes, conceptRes] = await Promise.all([
                    getPolicyParameters(policyCode, {
                        params: { limit: 500 }, signal: controller.signal,
                    }),
                    governed.length
                        ? conceptsApi.list(
                            { codes: governed.join(','), limit: 200 },
                            { signal: controller.signal })
                        : Promise.resolve(null),
                ]);
                if (!active) return;

                setParameters(Array.isArray(paramRes?.data?.parameters)
                    ? paramRes.data.parameters : []);

                if (conceptRes) {
                    const byCode: Record<string, Record<string, any>> = {};
                    for (const concept of normalizeList(conceptRes, 'concepts').documents) {
                        // A code can have several versions; the first is the one the API
                        // sorted highest, and any version's label will do here.
                        if (!byCode[concept.concept_code]) byCode[concept.concept_code] = concept;
                    }
                    setLabels(byCode);
                }
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error('Error loading policy parameters:', err);
                setError(ontologyErrorMessage(err, 'Could not load this policy\'s thresholds.'));
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [policyCode, governed]);

    const rows: CoverageRow[] = useMemo(() => {
        const byConcept = new Map<string, Record<string, any>[]>();
        for (const parameter of parameters) {
            const code = parameter.concept_code;
            if (!code) continue;
            // One (concept, policy) pair yields one parameter PER SCOPE, so a concept can
            // legitimately hold several here. Today's data has one each; the schema does
            // not promise that and the UI must not assume it.
            if (!byConcept.has(code)) byConcept.set(code, []);
            byConcept.get(code)!.push(parameter);
        }

        // Every governed concept, plus any concept a parameter names that is somehow not
        // in governsConcepts — that combination would be a data fault worth seeing rather
        // than hiding.
        const codes = [...new Set([...governed, ...byConcept.keys()])];

        return codes
            .map((code) => ({
                conceptCode: code,
                label: labels[code]?.preferred_label || code,
                conceptType: labels[code]?.concept_type,
                parameters: byConcept.get(code) ?? [],
            }))
            // Configured first: the useful rows must not sit below 23 empty ones.
            .sort((a, b) => (b.parameters.length > 0 ? 1 : 0) - (a.parameters.length > 0 ? 1 : 0)
                || a.label.localeCompare(b.label));
    }, [parameters, governed, labels]);

    if (!policyCode) return null;

    const configured = rows.filter((r) => r.parameters.length > 0).length;
    const unset = rows.length - configured;

    return (
        <Section title="Thresholds this policy sets" always>
            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: dsVar('fg-2') }}>
                    <CircularProgress size={14} sx={{ color: dsVar('accent-fg') }} />
                    <Typography sx={{ fontSize: '0.8125rem' }}>Loading thresholds…</Typography>
                </Box>
            )}

            {!loading && error && (
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{error}</Typography>
            )}

            {!loading && !error && rows.length === 0 && (
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                    This policy governs no concepts, so it sets no thresholds.
                </Typography>
            )}

            {!loading && !error && rows.length > 0 && (
                <>
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mb: 1.5 }}>
                        Governs {rows.length} concept{rows.length === 1 ? '' : 's'} ·{' '}
                        <Typography component="span" sx={{ color: dsVar('fg-body'), fontSize: '0.8125rem' }}>
                            {configured} configured
                        </Typography>
                        {unset > 0 && (
                            <>
                                {' · '}
                                <Typography component="span" sx={{ color: ink('#FBBF24'), fontSize: '0.8125rem' }}>
                                    {unset} with no threshold
                                </Typography>
                            </>
                        )}
                    </Typography>

                    <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '6px', overflow: 'hidden' }}>
                        {rows.map((row, index) => {
                            const hasParameters = row.parameters.length > 0;
                            const isOpen = Boolean(expanded[row.conceptCode]);
                            const href = conceptHref(row.conceptCode);

                            return (
                                <Box
                                    key={row.conceptCode}
                                    sx={{ borderTop: index === 0 ? 'none' : `1px solid ${dsVar('line')}` }}
                                >
                                    <Box
                                        onClick={hasParameters
                                            ? () => setExpanded((e) => ({
                                                ...e, [row.conceptCode]: !e[row.conceptCode],
                                            }))
                                            : undefined}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            px: 1.5, py: 1.25,
                                            cursor: hasParameters ? 'pointer' : 'default',
                                            backgroundColor: hasParameters ? 'transparent' : dsVar('page'),
                                            '&:hover': hasParameters ? { backgroundColor: dsVar('raised') } : {},
                                        }}
                                    >
                                        <Box sx={{ width: 18, flexShrink: 0, display: 'flex' }}>
                                            {hasParameters && (isOpen
                                                ? <ExpandMoreIcon sx={{ fontSize: 18, color: dsVar('fg-muted') }} />
                                                : <ChevronRightIcon sx={{ fontSize: 18, color: dsVar('fg-muted') }} />)}
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {href ? (
                                                <Box
                                                    component="a"
                                                    href={href}
                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        color: hasParameters ? dsVar('accent-fg') : dsVar('fg-2'),
                                                        textDecoration: 'none',
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                >
                                                    {row.label}
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                                                    {row.label}
                                                </Typography>
                                            )}
                                            <Typography component="div" sx={mono}>
                                                {row.conceptCode}
                                                {/* Through the shared label map, not raw:
                                                    this sat one screen away from a column
                                                    reading "Red flag" while printing
                                                    `risk_signal`. */}
                                                {row.conceptType ? ` · ${conceptTypeLabel(row.conceptType)}` : ''}
                                            </Typography>
                                        </Box>

                                        {hasParameters ? (
                                            <Typography
                                                sx={{
                                                    flexShrink: 0, fontSize: '0.875rem',
                                                    fontWeight: 600, color: dsVar('fg-body'),
                                                }}
                                            >
                                                {row.parameters.length === 1
                                                    ? formatValue(row.parameters[0].value, row.parameters[0].unit)
                                                    : `${row.parameters.length} values`}
                                            </Typography>
                                        ) : (
                                            <Chip
                                                size="small"
                                                label="not set"
                                                sx={{
                                                    height: 20, fontSize: '0.6875rem', flexShrink: 0,
                                                    color: ink('#FBBF24'), backgroundColor: 'transparent',
                                                    border: '1px solid rgba(251, 191, 36, 0.4)',
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Collapse in={isOpen} unmountOnExit>
                                        <Box sx={{ pl: 4.5, pr: 1.5, pb: 1.25 }}>
                                            {row.parameters.map((parameter, i) => (
                                                <ParameterLine key={parameter.parameter_code ?? i} parameter={parameter} />
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Section>
    );
};

/** One parameter under a concept, with the lifecycle facts that decide whether it bites. */
export const ParameterLine: React.FC<{ parameter: Record<string, any> }> = ({ parameter }) => {
    const href = parameterHref(parameter.parameter_code);
    const scope = parameter.scope || {};
    const declared = Object.entries(scope).filter(([, v]) => v);

    return (
        <Box sx={{ py: 0.75, borderTop: `1px solid ${dsVar('line')}` }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                {href ? (
                    <Box
                        component="a"
                        href={href}
                        sx={{
                            flex: 1, minWidth: 0, fontSize: '0.8125rem', color: dsVar('fg-soft'),
                            textDecoration: 'none', wordBreak: 'break-word',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {parameter.parameter_code}
                    </Box>
                ) : (
                    <Typography sx={{ flex: 1, fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                        {parameter.parameter_code}
                    </Typography>
                )}
                <Typography sx={{ flexShrink: 0, fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 600 }}>
                    {formatValue(parameter.value, parameter.unit)}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                <InForceBadge parameter={parameter} />
                {declared.length > 0 && (
                    <Typography component="span" sx={{ ...mono, wordBreak: 'break-all' }}>
                        {declared.map(([k, v]) => `${k}=${v}`).join(' · ')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

/**
 * Whether this value actually governs anything right now.
 *
 * `in_force` is computed by the API, not here. "Approved" is a different question: the
 * US.BSA.CTR baseline is approved with an effective window that never opens, and a status
 * chip on its own reports that one as live.
 */
export const InForceBadge: React.FC<{ parameter: Record<string, any> }> = ({ parameter }) => {
    const inForce = parameter.in_force === true;
    const approved = parameter.status === 'approved';

    const label = inForce ? 'in force'
        : approved ? 'approved · not in force'
            : 'draft · not yet in force';
    const color = inForce ? '#4ADE80' : approved ? '#94A3B8' : '#FBBF24';

    return (
        <Typography
            component="span"
            sx={{
                fontSize: '0.6875rem', letterSpacing: '0.03em', color,
                textTransform: 'uppercase',
            }}
        >
            {label}
        </Typography>
    );
};

export default PolicyParametersSection;
